import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "./assets/logo.png";
import pezzulLogo from "./assets/pezzul.png";
import AppStore from "./components/AppStore/AppStore";
import MoreApps from "./components/MoreApps/MoreApps";
import SocialUpdates from "./components/SocialUpdates/SocialUpdates";
import FAQ from "./components/FAQ/FAQ";
import ViewportVideo from "./components/ViewportVideo/ViewportVideo";
import "./App.css";

const SOUND_PREFERENCE_KEY = "mnezy:sound";
const BETA_TINT_PREFERENCE_KEY = "mnezy:testflight-tint";
const MNEZY_TINTS = ["DA5F4E", "F3D864", "83CC75", "4F81EE"];

const createBetaGuideUrl = () => {
  let previousTint;

  try {
    previousTint = window.sessionStorage.getItem(BETA_TINT_PREFERENCE_KEY);
  } catch {
    // Tint rotation still works when browser storage is unavailable.
  }

  const availableTints = MNEZY_TINTS.filter((tint) => tint !== previousTint);
  const tint = availableTints[Math.floor(Math.random() * availableTints.length)];

  try {
    window.sessionStorage.setItem(BETA_TINT_PREFERENCE_KEY, tint);
  } catch {
    // Remembering the previous tint is an optional visual enhancement.
  }

  return `https://testflight.marclaberge.org/?app=MNEZY&invite=email&tint=${tint}`;
};

const BETA_GUIDE_URL = createBetaGuideUrl();

const appSocialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/mnezyapp/" },
  { label: "X", href: "https://x.com/mnezyapp" },
  { label: "Threads", href: "https://www.threads.net/@mnezyapp" },
];

const creatorSocialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/marc-antoine-laberge" },
  { label: "GitHub", href: "https://github.com/marcgeeklaberge" },
  { label: "Instagram", href: "https://www.instagram.com/marclaberge.ml/" },
  { label: "YouTube", href: "https://www.youtube.com/MarcLabergeML" },
  { label: "X", href: "https://x.com/MarcLabergeML" },
  { label: "Threads", href: "https://www.threads.net/@marclaberge.ml" },
];

const copy = {
  en: {
    nav: {
      play: "How it plays",
      everywhere: "Everywhere",
      gallery: "Gallery",
    },
    hero: {
      eyebrow: "Memory, sharpened.",
      title: "How far can your memory take you?",
      body: "Watch the colour sequence. Repeat it. Then go one step further. MNEZY turns a four-colour classic into a focused challenge made for your Apple devices.",
      note: "$1.99 CAD · Age 4+ · English & French",
      rating: "5.0 on the App Store",
      ratings: "4 ratings",
      version: "Version 2.2.4",
      updated: "Updated October 2026",
    },
    play: {
      eyebrow: "How it plays",
      title: "Simple to learn. Hard to put down.",
      body: "MNEZY keeps the rules clear and lets your concentration do the work.",
      cards: [
        {
          number: "01",
          title: "Watch. Repeat. Level up.",
          body: "Follow the colour pattern, tap it back in order, and keep the sequence alive as it grows.",
          tone: "red",
        },
        {
          number: "02",
          title: "Choose your challenge.",
          body: "Classic mode builds one sequence. Hard mode gives you a fresh pattern every round.",
          tone: "yellow",
        },
        {
          number: "03",
          title: "Set your rhythm.",
          body: "Choose slow, normal, or fast playback. Hear each colour as the sequence plays, with separate sequence-sound settings and haptics on supported devices.",
          tone: "green",
        },
      ],
    },
    everywhere: {
      eyebrow: "Across Apple",
      title: "One game. Every Apple screen.",
      body: "Start a quick round wherever you are. MNEZY is designed for iPhone, iPad, Apple Watch, Apple TV, and Apple Vision Pro—with widgets, Live Activities, Siri, Shortcuts, and Spotlight woven in.",
      devices: ["iPhone", "iPhone Duo", "iPad", "Apple Watch", "Apple TV", "Apple Vision Pro"],
    },
    gallery: {
      eyebrow: "Five Apple platforms",
      title: "One game, shaped for every screen.",
      body: "From a quick round on your wrist to a full-screen challenge in the living room, MNEZY keeps the same four-colour focus while adapting to every device.",
      shots: [
        { src: "/screenshots/iphone-light-en.jpg", alt: "MNEZY classic game on iPhone", device: "iPhone", label: "Classic play", frame: "phone" },
        { src: "/screenshots/live-current-en.jpg", alt: "MNEZY Live Activity on the iPhone Lock Screen", device: "iPhone", label: "Live Activities", frame: "phone" },
        { src: "/screenshots/ipad-current-en.jpg", alt: "MNEZY game board and controls on iPad", device: "iPad", label: "Room to focus", frame: "tablet" },
        { src: "/screenshots/watch-current-en.jpg", alt: "MNEZY colour game on Apple Watch", device: "Apple Watch", label: "A round on your wrist", frame: "watch" },
        { src: "/screenshots/duo-en.jpg", alt: "MNEZY on unfolded iPhone Duo in landscape", device: "iPhone Duo", label: "Unfold your next challenge", frame: "duo" },
        { src: "/screenshots/tv-en.jpg", alt: "MNEZY colour game on Apple TV", device: "Apple TV", label: "Play on the big screen", frame: "tv" },
        { src: "/screenshots/vision-en.jpg", alt: "MNEZY colour game in a living room on Apple Vision Pro", device: "Apple Vision Pro", label: "Bring the game into your space", frame: "wide" },
      ],
    },
    videos: {
      title: "See MNEZY in motion.",
      body: "Watch the game on iPhone, iPhone Duo, iPad, and Apple Watch. Play a preview to follow a round.",
      fallback: "Download the video",
      updateTitle: "A new way to see your next round.",
      updateBody: "Transparent Mode adds an optional blurred rear-camera background on iPhone. It stays off until you enable it; camera images are never recorded or saved. Updated layouts also adapt to iPhone Duo in every posture.",
    },
    proof: {
      items: [
        { value: "5.0", label: "App Store rating", detail: "from 4 ratings" },
        { value: "2.2.4", label: "Current version", detail: "released October 2026" },
        { value: "4+", label: "Age rating", detail: "made for quick, clear play" },
      ],
      accessTitle: "Made for more players.",
      accessBody: "VoiceOver labels, Siri and Shortcuts help you get into a round. Live Activities keep game status close, while Game Center brings leaderboards and achievements across supported Apple devices.",
    },
    closing: {
      eyebrow: "Ready for one more round?",
      title: "Your next sequence is waiting.",
      body: "Play MNEZY across your Apple devices and see where your focus takes you.",
    },
    moreApps: {
      eyebrow: "More apps by Marc Laberge",
      title: "Independent ideas, built with care.",
      body: "MNEZY is part of a growing collection of focused apps—each with a simple idea at its centre.",
      current: "Available",
      currentBody: "Four colours. Your memory.",
      pezzulBody: "Turn the images you love into puzzles you’ll love to solve.",
      pezzulStatus: "Available",
      next: "More to come",
      nextBody: "Another small challenge is taking shape.",
      nextStatus: "In the works",
    },
    socials: {
      app: "Follow MNEZY",
      creator: "Follow Marc",
    },
    sound: {
      on: "Sound on",
      off: "Sound off",
      turnOn: "Turn the MNEZY soundtrack on",
      turnOff: "Turn the MNEZY soundtrack off",
    },
    footer: "Designed and built by Marc-Antoine Laberge.",
    betaGuide: "Beta tester guide",
    relatedLinks: "Related links",
  },
  fr: {
    nav: {
      play: "Comment jouer",
      everywhere: "Partout",
      gallery: "Aperçu",
    },
    hero: {
      eyebrow: "Votre mémoire, aiguisée.",
      title: "Jusqu’où votre mémoire peut-elle vous mener?",
      body: "Observez la séquence de couleurs. Reproduisez-la. Puis allez un peu plus loin. MNEZY transforme un grand classique en un défi épuré pour vos appareils Apple.",
      note: "1,99 $ CA · 4 ans et plus · Français et anglais",
      rating: "5,0 sur l’App Store",
      ratings: "4 évaluations",
      version: "Version 2.2.4",
      updated: "Mise à jour en octobre 2026",
    },
    play: {
      eyebrow: "Comment jouer",
      title: "Facile à apprendre. Difficile à déposer.",
      body: "MNEZY garde les règles simples et laisse votre concentration faire le reste.",
      cards: [
        {
          number: "01",
          title: "Observez. Répétez. Progressez.",
          body: "Suivez la séquence, reproduisez-la dans l’ordre et gardez le rythme à mesure qu’elle s’allonge.",
          tone: "red",
        },
        {
          number: "02",
          title: "Choisissez votre défi.",
          body: "Le mode Classique allonge la même séquence. Le mode Difficile en crée une nouvelle à chaque tour.",
          tone: "yellow",
        },
        {
          number: "03",
          title: "Trouvez votre rythme.",
          body: "Choisissez une vitesse lente, normale ou rapide. Écoutez chaque couleur pendant la séquence, avec un réglage sonore distinct et des vibrations sur les appareils compatibles.",
          tone: "green",
        },
      ],
    },
    everywhere: {
      eyebrow: "Dans l’écosystème Apple",
      title: "Un jeu. Tous vos écrans Apple.",
      body: "Lancez une partie où que vous soyez. MNEZY est conçu pour iPhone, iPad, Apple Watch, Apple TV et Apple Vision Pro, avec des widgets, Activités en direct, Siri, Raccourcis et Spotlight.",
      devices: ["iPhone", "iPhone Duo", "iPad", "Apple Watch", "Apple TV", "Apple Vision Pro"],
    },
    gallery: {
      eyebrow: "Sur cinq plateformes Apple",
      title: "Un jeu, adapté à chaque écran.",
      body: "D’une partie rapide au poignet à un défi plein écran au salon, MNEZY conserve la même concentration autour de quatre couleurs tout en s’adaptant à chaque appareil.",
      shots: [
        { src: "/screenshots/iphone-light-fr.jpg", alt: "Partie classique MNEZY sur iPhone", device: "iPhone", label: "Partie classique", frame: "phone" },
        { src: "/screenshots/live-current-fr.jpg", alt: "Activité en direct MNEZY sur l’écran verrouillé de l’iPhone", device: "iPhone", label: "Activités en direct", frame: "phone" },
        { src: "/screenshots/ipad-current-fr.jpg", alt: "Plateau de jeu et commandes MNEZY sur iPad", device: "iPad", label: "Plus d’espace pour se concentrer", frame: "tablet" },
        { src: "/screenshots/watch-current-fr.jpg", alt: "Jeu de couleurs MNEZY sur Apple Watch", device: "Apple Watch", label: "Une partie au poignet", frame: "watch" },
        { src: "/screenshots/duo-fr.jpg", alt: "MNEZY sur iPhone Duo déplié en mode paysage", device: "iPhone Duo", label: "Dépliez votre prochain défi", frame: "duo" },
        { src: "/screenshots/tv-fr.jpg", alt: "Jeu de couleurs MNEZY sur Apple TV", device: "Apple TV", label: "Jouez sur grand écran", frame: "tv" },
        { src: "/screenshots/vision-fr.jpg", alt: "Jeu de couleurs MNEZY dans un salon sur Apple Vision Pro", device: "Apple Vision Pro", label: "Invitez le jeu dans votre espace", frame: "wide" },
      ],
    },
    videos: {
      title: "Découvrez MNEZY en action.",
      body: "Regardez le jeu sur iPhone, iPhone Duo, iPad et Apple Watch. Lancez un aperçu pour suivre une partie.",
      fallback: "Télécharger la vidéo",
      updateTitle: "Une nouvelle perspective sur votre partie.",
      updateBody: "Le mode Transparent ajoute un arrière-plan flouté de la caméra arrière sur iPhone. Il reste désactivé jusqu’à ce que vous l’activiez; les images ne sont jamais enregistrées ni sauvegardées. Les dispositions s’adaptent aussi à chaque posture de l’iPhone Duo.",
    },
    proof: {
      items: [
        { value: "5,0", label: "Note App Store", detail: "selon 4 évaluations" },
        { value: "2.2.4", label: "Version actuelle", detail: "parue en octobre 2026" },
        { value: "4+", label: "Classement d’âge", detail: "un jeu rapide et clair" },
      ],
      accessTitle: "Pensé pour plus de joueurs.",
      accessBody: "Les libellés VoiceOver, Siri et Raccourcis facilitent le lancement d’une partie. Les Activités en direct gardent votre progression à portée de vue, et Game Center propose classements et réalisations sur les appareils Apple compatibles.",
    },
    closing: {
      eyebrow: "Prêt pour une autre partie?",
      title: "Votre prochaine séquence vous attend.",
      body: "Jouez à MNEZY sur vos appareils Apple et voyez jusqu’où votre concentration peut vous mener.",
    },
    moreApps: {
      eyebrow: "D’autres apps de Marc Laberge",
      title: "Des idées indépendantes, créées avec soin.",
      body: "MNEZY fait partie d’une collection grandissante d’apps ciblées, chacune construite autour d’une idée simple.",
      current: "Disponible",
      currentBody: "Quatre couleurs. Votre mémoire.",
      pezzulBody: "Transformez les images que vous aimez en casse-têtes que vous adorerez résoudre.",
      pezzulStatus: "Disponible",
      next: "D’autres à venir",
      nextBody: "Un nouveau petit défi prend forme.",
      nextStatus: "En création",
    },
    socials: {
      app: "Suivre MNEZY",
      creator: "Suivre Marc",
    },
    sound: {
      on: "Son activé",
      off: "Son désactivé",
      turnOn: "Activer la trame sonore de MNEZY",
      turnOff: "Désactiver la trame sonore de MNEZY",
    },
    footer: "Conçu et développé par Marc-Antoine Laberge.",
    betaGuide: "Guide pour les testeurs bêta",
    relatedLinks: "Liens connexes",
  },
};

const deviceFrames = {
  phone: "/frames/iphone.png",
  duo: "/frames/duo.png",
  tv: "/frames/tv.png",
  tablet: "/frames/ipad.png",
  watch: "/frames/watch.png",
};

function DeviceScreen({ src, alt, frame = "phone", loading }) {
  return <div className={`device-screen device-screen--${frame}`}>
    <img className="device-screen__capture" src={src} alt={alt} loading={loading} />
    <img className="device-screen__frame" src={deviceFrames[frame]} alt="" aria-hidden="true" loading={loading} />
  </div>;
}

function BrandMark({ compact = false }) {
  return (
    <span className={`brand-mark${compact ? " brand-mark--compact" : ""}`} aria-hidden="true">
      <img src={logo} alt="" />
    </span>
  );
}

function App() {
  const { i18n } = useTranslation();
  const audioRef = useRef(null);
  const [locale, setLocale] = useState(() => (
    (i18n.resolvedLanguage || i18n.language)?.startsWith("fr") ? "fr" : "en"
  ));
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return window.localStorage.getItem(SOUND_PREFERENCE_KEY) === "on";
    } catch {
      return false;
    }
  });
  const [soundPlaying, setSoundPlaying] = useState(false);
  const c = copy[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
    if (!i18n.language?.startsWith(locale)) {
      void i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    if (!soundOn) {
      audio.pause();
      setSoundPlaying(false);
      return undefined;
    }

    let cancelled = false;
    audio.volume = 0.42;

    const attemptPlay = async () => {
      try {
        await audio.play();
        if (!cancelled) {
          setSoundPlaying(true);
        }
      } catch {
        if (!cancelled) setSoundPlaying(false);
      }
    };

    const handleUnlock = (event) => {
      if (event.target instanceof Element && event.target.closest(".sound-toggle")) return;
      void attemptPlay();
      window.removeEventListener("pointerdown", handleUnlock);
      window.removeEventListener("keydown", handleUnlock);
    };

    void attemptPlay();
    window.addEventListener("pointerdown", handleUnlock);
    window.addEventListener("keydown", handleUnlock);

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", handleUnlock);
      window.removeEventListener("keydown", handleUnlock);
    };
  }, [soundOn]);

  const toggleSound = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (soundOn) {
      audio.pause();
      setSoundOn(false);
      setSoundPlaying(false);
      try {
        window.localStorage.setItem(SOUND_PREFERENCE_KEY, "off");
      } catch {
        // The preference is optional when storage is unavailable.
      }
      return;
    }

    audio.volume = 0.42;
    setSoundOn(true);
    try {
      window.localStorage.setItem(SOUND_PREFERENCE_KEY, "on");
    } catch {
      // The preference is optional when storage is unavailable.
    }
    try {
      await audio.play();
      setSoundPlaying(true);
    } catch {
      setSoundPlaying(false);
    }
  };

  return (
    <div className="site-shell" id="top">
      <header className="site-header">
        <a className="brand-lockup" href="#top" aria-label="MNEZY home">
          <BrandMark compact />
          <span>MNEZY</span>
        </a>

        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#how-it-plays">{c.nav.play}</a>
          <a href="#everywhere">{c.nav.everywhere}</a>
          <a href="#gallery">{c.nav.gallery}</a>
        </nav>

        <details className="mobile-nav">
          <summary aria-label={locale === "fr" ? "Menu de navigation" : "Navigation menu"}>☰</summary>
          <nav aria-label={locale === "fr" ? "Navigation mobile" : "Mobile navigation"} onClick={(event) => { if (event.target.closest("a")) event.currentTarget.closest("details").open = false; }}>
            <a href="#how-it-plays">{c.nav.play}</a>
            <a href="#everywhere">{c.nav.everywhere}</a>
            <a href="#gallery">{c.nav.gallery}</a>
            <a href="#faq">FAQ</a>
          </nav>
        </details>

        <div className="language-switcher" aria-label="Language">
          <button type="button" className={locale === "en" ? "is-active" : ""} onClick={() => setLocale("en")} aria-label="View site in English" aria-pressed={locale === "en"}>EN</button>
          <button type="button" className={locale === "fr" ? "is-active" : ""} onClick={() => setLocale("fr")} aria-label="Voir le site en français" aria-pressed={locale === "fr"}>FR</button>
        </div>
      </header>

      <main>
        <section className="hero section-wrap" aria-labelledby="hero-title">
          <div className="hero__copy">
            <p className="eyebrow"><span className="eyebrow__dots" aria-hidden="true">••••</span>{c.hero.eyebrow}</p>
            <h1 id="hero-title">{c.hero.title}</h1>
            <p className="hero__lede">{c.hero.body}</p>
            <div className="hero__actions">
              <AppStore locale={locale} />
              <span className="hero__note">{c.hero.note}</span>
            </div>
            <dl className="hero__trust" aria-label="App Store details">
              <div><dt>{c.hero.rating}</dt><dd>{c.hero.ratings}</dd></div>
              <div><dt>{c.hero.version}</dt><dd>{c.hero.updated}</dd></div>
            </dl>
          </div>

          <div className="hero__stage" aria-label="MNEZY app preview">
            <div className="hero__glow hero__glow--red" />
            <div className="hero__glow hero__glow--blue" />
            <div className="hero__screen hero__screen--back" aria-hidden="true">
              <DeviceScreen src={`/screenshots/iphone-dark-${locale}.jpg`} alt="" />
            </div>
            <div className="hero__screen hero__screen--front">
              <DeviceScreen src={`/screenshots/iphone-light-${locale}.jpg`} alt={locale === "fr" ? "Plateau MNEZY à quatre couleurs" : "MNEZY game board showing four colour tiles"} />
            </div>
            <div className="hero__app-icon">
              <img src={logo} alt="MNEZY app icon" />
            </div>
          </div>
        </section>

        <section className="play-section section-wrap" id="how-it-plays" aria-labelledby="play-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{c.play.eyebrow}</p>
              <h2 id="play-title">{c.play.title}</h2>
            </div>
            <p>{c.play.body}</p>
          </div>
          <div className="feature-grid">
            {c.play.cards.map((card) => (
              <article className={`feature-card feature-card--${card.tone}`} key={card.number}>
                <span className="feature-card__number">{card.number}</span>
                <div className="feature-card__mini-grid" aria-hidden="true"><i /><i /><i /><i /></div>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ecosystem section-wrap" id="everywhere" aria-labelledby="ecosystem-title">
          <div className="ecosystem__copy">
            <p className="eyebrow eyebrow--light">{c.everywhere.eyebrow}</p>
            <h2 id="ecosystem-title">{c.everywhere.title}</h2>
            <p>{c.everywhere.body}</p>
            <ul className="device-list" aria-label="Supported devices">
              {c.everywhere.devices.map((device) => <li key={device}>{device}</li>)}
            </ul>
          </div>
          <div className="ecosystem__visual" aria-hidden="true">
            <div className="focus-orbit focus-orbit--one" />
            <div className="focus-orbit focus-orbit--two" />
            <div className="focus-orbit focus-orbit--three" />
            <div className="ecosystem__mark"><BrandMark /></div>
            <div className="ecosystem__satellite" style={{ "--phase": "-55deg" }}><span className="ecosystem__pill">Siri</span></div>
            <div className="ecosystem__satellite ecosystem__satellite--outer" style={{ "--phase": "135deg" }}><span className="ecosystem__pill">{locale === "fr" ? "Activités en direct" : "Live Activities"}</span></div>
            <div className="ecosystem__satellite ecosystem__satellite--middle ecosystem__satellite--reverse" style={{ "--phase": "75deg" }}><span className="ecosystem__pill">Widgets</span></div>
          </div>
        </section>

        <section className="gallery-section" id="gallery" aria-labelledby="gallery-title">
          <div className="section-wrap">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{c.gallery.eyebrow}</p>
                <h2 id="gallery-title">{c.gallery.title}</h2>
              </div>
              <p>{c.gallery.body}</p>
            </div>
          </div>
          <div className="gallery-rail" role="list" aria-label="MNEZY screenshots">
            {c.gallery.shots.map((shot, index) => (
              <figure className={`gallery-card gallery-card--${shot.frame} gallery-card--${index + 1}`} key={shot.src} role="listitem">
                <div className="gallery-card__image">{deviceFrames[shot.frame]
                  ? <DeviceScreen src={shot.src} alt={shot.alt} frame={shot.frame} loading={index > 1 ? "lazy" : "eager"} />
                  : <img src={shot.src} alt={shot.alt} loading="lazy" />}</div>
                <figcaption><span className="gallery-card__device">{shot.device}</span><span>{shot.label}</span></figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="video-section section-wrap" aria-labelledby="video-title">
          <div className="section-heading">
            <h2 id="video-title">{c.videos.title}</h2>
            <p>{c.videos.body}</p>
          </div>
          <div className="video-grid">
            {[{ id: "iphone", label: "iPhone", frame: "phone" }, { id: "duo", label: "iPhone Duo", frame: "duo" }, { id: "ipad", label: "iPad", frame: "tablet" }, { id: "watch", label: "Apple Watch", frame: "watch" }].map((video) => (
              <figure className={`video-card${video.id === "duo" ? " video-card--duo" : ""}`} key={`${video.id}-${locale}`}>
                <div className="video-stage">
                  <div className={`device-screen device-screen--${video.frame}`}>
                <ViewportVideo className="device-screen__capture" poster={`/videos/${video.id}-${locale}.jpg`} aria-label={`${video.label} — ${c.videos.title}`}>
                  <source src={`/videos/${video.id}-${locale}.mp4`} type="video/mp4" />
                  <a href={`/videos/${video.id}-${locale}.mp4`}>{c.videos.fallback}</a>
                </ViewportVideo>
                    <img className="device-screen__frame" src={deviceFrames[video.frame]} alt="" aria-hidden="true" loading="lazy" />
                  </div>
                </div>
                <figcaption>{video.label}</figcaption>
              </figure>
            ))}
          </div>
          <div className="release-note"><h3>{c.videos.updateTitle}</h3><p>{c.videos.updateBody}</p></div>
        </section>

        <section className="proof section-wrap" aria-label="MNEZY details">
          <div className="proof__stats">
            {c.proof.items.map((item) => (
              <div className="proof-stat" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
                <small>{item.detail}</small>
              </div>
            ))}
          </div>
          <div className="accessibility-card">
            <div className="accessibility-card__icon" aria-hidden="true">◎</div>
            <div><h2>{c.proof.accessTitle}</h2><p>{c.proof.accessBody}</p></div>
          </div>
        </section>

        <section className="closing section-wrap" aria-labelledby="closing-title">
          <div className="closing__tiles" aria-hidden="true"><BrandMark /></div>
          <div className="closing__copy">
            <p className="eyebrow eyebrow--light">{c.closing.eyebrow}</p>
            <h2 id="closing-title">{c.closing.title}</h2>
            <p>{c.closing.body}</p>
            <AppStore locale={locale} inverted />
          </div>
        </section>

        <SocialUpdates locale={locale} links={appSocialLinks} />

        <FAQ locale={locale} />

        <MoreApps copy={c.moreApps} logo={logo} pezzulLogo={pezzulLogo} />
      </main>

      <footer className="site-footer section-wrap">
        <div className="site-footer__main">
          <a className="brand-lockup" href="#top" aria-label={locale === "fr" ? "Retour en haut" : "Back to top"}><BrandMark compact /><span>MNEZY</span></a>
          <nav className="site-footer__links" aria-label={c.relatedLinks}>
            <a href="#privacy">{locale === "fr" ? "Confidentialité" : "Privacy"}</a>
            <a href="#faq">FAQ</a>
            <details className="footer-more">
              <summary>{locale === "fr" ? "Autres liens" : "More links"}</summary>
              <nav aria-label={locale === "fr" ? "Liens supplémentaires" : "Additional links"}>
                <a href={BETA_GUIDE_URL} target="_blank" rel="noreferrer">{c.betaGuide} ↗</a>
                <a href="https://apps.apple.com/ca/developer/marc-antoine-laberge/id1513672557" target="_blank" rel="noreferrer">App Store ↗</a>
                {creatorSocialLinks.map((social) => <a key={social.label} href={social.href} target="_blank" rel="noreferrer" aria-label={`Marc on ${social.label}`}>{social.label} ↗</a>)}
              </nav>
            </details>
          </nav>
          <p>{c.footer}</p>
        </div>
      </footer>

      <audio ref={audioRef} loop preload="metadata">
        <source src="/audio/mnezy-theme.mp3" type="audio/mpeg" />
      </audio>
      <button
        type="button"
        className={`sound-toggle${soundPlaying ? " is-playing" : ""}`}
        onClick={toggleSound}
        aria-label={soundOn ? c.sound.turnOff : c.sound.turnOn}
        aria-pressed={soundOn}
      >
        <span className="sound-toggle__bars" aria-hidden="true"><i /><i /><i /></span>
        <span>{soundOn ? c.sound.on : c.sound.off}</span>
      </button>
    </div>
  );
}

export default App;
